import * as Sentry from '@sentry/react';
import { log } from '../utils/logger';
import type { AuthResponse, LoginInput, PublicProfile, RegisterInput, RegisterResponse, UpgradeInput, User } from '../types/auth';
import type { RispostaAvviso } from '@draughts/shared';
import type {
  GameDetails,
  GamesFilters,
  GamesListResponse,
  LeaderboardResponse,
  MostGamesResponse,
  StreakResponse,
  TimeControlCategory,
  UserBadge,
  UserStatsResponse,
  RatingDetailResponse,
} from '../types/games';
import type {
  TournamentListItem,
  TournamentDetail,
  TournamentGameEntry,
  CreateTournamentInput,
  CreatedTournament,
} from '../types/tournament';
import type {
  ArenaListItem,
  CreateArenaInput,
  CreatedArena,
  ArenaDetail,
  ArenaGameEntry,
  ArenaStandingEntry,
} from '../types/arena';
import type { UserPreferences } from '@draughts/shared';
import { NETWORK_ERROR_CODE } from './network-error';

/* Definito in un file suo (vedi `api-url.ts` per il perche': il prerender delle
   pagine regole non deve tirarsi dietro questo client) e ri-esportato qui, cosi'
   chi lo importava da `services/api` non cambia una riga.

   Importato E ri-esportato: un `export … from` non crea il binding locale, e qui
   sotto `API_URL` serve davvero, quattro volte. */
import { API_URL } from './api-url';
export { API_URL };

/* Il sentinella della rete caduta vive in un file suo (vedi `network-error.ts`
   per il perché); qui si riesporta, così chi lo importava da `api` non cambia
   una riga. */
export { NETWORK_ERROR_CODE } from './network-error';

interface ApiError {
  error: string;
  message: string;
  details?: Record<string, string[]>;
}

/**
 * An Error carrying the HTTP status and the server's error code alongside the
 * message. Callers that must distinguish failure kinds — a 409 "folder name
 * already taken" from a plain network drop, say — read `.status`/`.code`;
 * everyone else keeps using `.message` exactly as before.
 */
export interface ApiRequestError extends Error {
  status?: number;
  code?: string;
  /**
   * `true` quando `request` l'ha gia' mandato a Sentry (i 5xx). Chi lo riceve
   * non lo rimanda: ogni chiamata fallita arrivava due volte, `API 502: …` da
   * qui e `Request failed` dal chiamante.
   */
  segnalatoASentry?: boolean;
}

function buildApiError(message: string, status: number, code?: string): ApiRequestError {
  const err = new Error(message) as ApiRequestError;
  err.status = status;
  err.code = code;
  return err;
}

/**
 * Come e' andato un rinnovo del token. Quattro esiti, perche' quattro cose
 * diverse:
 *
 * - `'riuscito'`      — c'e' un access token nuovo;
 * - `'credenziali-morte'` — non c'e' un refresh token utilizzabile, e nessun
 *   ritentativo lo resusciterebbe. Qui, e solo qui, si va alla schermata di
 *   accesso. Tre codici, per due motivi diversi: 401/403 sono il server che ha
 *   GUARDATO il token e l'ha rifiutato (scaduto o revocato); il 400 e'
 *   `MISSING_REFRESH_TOKEN`, cioe' il server che dice che un token non gliene e'
 *   proprio arrivato — il cookie non c'e'.
 *
 *   Il 400 stava fra i transitori, ed era il vicolo cieco che questo ramo
 *   chiude: «nessun cookie» e' lo stato di credenziali PIU' terminale che
 *   esista, non uno da riprovare. Trattato come transitorio lasciava lo store
 *   con `isAuthenticated: true` (persistito) e nessun token (non persistito),
 *   cioe' una sessione fantasma che ad ogni chiamata riprovava il rinnovo,
 *   senza schermata di accesso ne' modo di uscirne; dopo trenta tentativi in
 *   un quarto d'ora arrivava il 429 del limitatore e murava anche la via di
 *   ritorno. Nessun ritentativo puo' far comparire un cookie che non c'e';
 * - `'transitorio'`   — il rinnovo non e' riuscito ma le credenziali non
 *   c'entrano: 500/502/503, oppure `fetch` che lancia perche' la rete non
 *   risponde. Chi chiama puo' ritentare; lo stato di autenticazione resta;
 * - `'rallenta'`      — 429 dal rate limiter. Come il transitorio per la
 *   decisione di sloggiare (le credenziali non c'entrano, nessuno esce
 *   dall'account), ma l'OPPOSTO per la decisione di ritentare: e' il server che
 *   dice «stai chiedendo troppo», e insistere lo peggiora e basta. Prima era
 *   `transitorio` e quindi veniva ritentato: un rientro sfortunato bruciava la
 *   finestra del limitatore e il ritentativo aggiungeva richieste su un limite
 *   gia' esaurito.
 *
 * Prima erano tutti `false`, e chi lo leggeva sloggiava l'utente.
 */
export type EsitoRinnovo = 'riuscito' | 'credenziali-morte' | 'transitorio' | 'rallenta';

/**
 * Tetto sulla SINGOLA richiesta di rinnovo.
 *
 * `fetch` senza `AbortController` non scade mai: una rete che non risponde e
 * non fallisce lascia la richiesta appesa, e siccome il rinnovo e' deduplicato
 * (`refreshPromise` condivisa) ogni rinnovo successivo si accoda a quella
 * piantata — per il resto della sessione. Chi sta rientrando in partita
 * resterebbe senza identita' fino alla sconfitta a tavolino, ed e' esattamente
 * il vicolo cieco che questo ramo ripara, un piano piu' sotto.
 *
 * Col tetto la richiesta muore, la promessa condivisa si libera e la successiva
 * PARTE.
 *
 * SEI secondi, non due. A due il taglio buttava rinnovi che stavano riuscendo:
 * il 30 settembre, su un Android con rete lenta, la richiesta e' arrivata al
 * server 2,2 s dopo la partenza — il server ha rinnovato, il client l'aveva
 * gia' abbandonata, e la dashboard e' rimasta 20 secondi senza token fino al
 * `TIMEOUT` (Sentry DRAUGHTS-FRONTEND-P). Il rientro in partita non ne risente:
 * il suo confine e' `ATTESA_MASSIMA_RINNOVO_MS` in `socket.ts`, un tetto
 * esterno che scade prima di questo, ed e' quello che entra nel conto del
 * grace period.
 */
export const TETTO_RICHIESTA_RINNOVO_MS = 6_000;

class ApiClient {
  private accessToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<EsitoRinnovo> | null = null;
  /**
   * L'esito dell'ultimo rinnovo concluso. Lo legge chi RITENTA (il socket) per
   * distinguere «riprova» da «rallenta»: `tryRefresh` restituisce un booleano e
   * quei due casi ci arrivano identici.
   */
  ultimoEsitoRinnovo: EsitoRinnovo | null = null;
  private onTokenRefreshed: ((token: string) => void) | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setOnTokenRefreshed(callback: (token: string) => void): void {
    this.onTokenRefreshed = callback;
  }

  /**
   * Authenticated calls for feature modules that own their own endpoint surface
   * (e.g. puzzle-api). They must go through request(), not a bare fetch(): only
   * request() carries the 401 → refresh → retry interceptor, so a bare fetch
   * silently fails every call once the access token expires.
   */
  async get<T>(endpoint: string, opts?: { signal?: AbortSignal }): Promise<T> {
    return this.request<T>(endpoint, { signal: opts?.signal });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    });
  }

  /** `del`, not `delete`: a reserved word reads badly as a method name. */
  async del<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, isRetry = false): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response: Response;
    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include', // For cookies (refresh token)
      });
    } catch (err) {
      // Only a network-level failure reaches here: fetch() rejects with a TypeError
      // when offline/unreachable. Normalize to a stable code. Rethrow everything else
      // (notably AbortError DOMExceptions from AbortController) so signal-based
      // cancellation handling downstream keeps working.
      if (err instanceof TypeError) {
        log.warn('API', 'network request failed (offline?)', { endpoint });
        throw new Error(NETWORK_ERROR_CODE, { cause: err });
      }
      throw err;
    }

    if (!response.ok) {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        log.warn('API', 'error response body was not JSON', { endpoint, status: response.status });
        errorData = { error: 'UNKNOWN', message: 'Request failed' };
      }

      /*
       * 502/503/504 sono il GATEWAY: Nginx non trova il processo Node dietro di
       * se', tipicamente per i secondi del deploy (`pm2 delete` + `start`). Non
       * e' un bug del codice, e il server giu' lo sorveglia gia' UptimeRobot: in
       * Sentry resta contato, ma come avviso. Il 500 invece e' un guasto vero
       * del server e resta un errore.
       */
      const segnalatoASentry = response.status >= 500;
      if (segnalatoASentry) {
        const gateway = response.status === 502 || response.status === 503 || response.status === 504;
        if (gateway) log.warn('API', `Gateway ${response.status} (server irraggiungibile): ${endpoint}`);
        else log.error('API', `Server error ${response.status}: ${endpoint}`);
        Sentry.captureException(new Error(`API ${response.status}: ${endpoint}`), {
          level: gateway ? 'warning' : 'error',
          tags: { gateway: String(gateway), status: String(response.status) },
        });
      }
      const lancia = (messaggio: string): never => {
        const errore = buildApiError(messaggio, response.status, errorData.error);
        if (segnalatoASentry) errore.segnalatoASentry = true;
        throw errore;
      };

      // Auto-refresh on 401 (token expired) - but not for login/register/refresh endpoints
      const skipRefresh = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/verify', '/auth/resend-verification', '/auth/forgot-password', '/auth/reset-password'].some(p => endpoint.startsWith(p));
      if (response.status === 401 && !isRetry && !skipRefresh) {
        log.warn('API', '401 received, attempting token refresh', { endpoint, isRetry });
        const refreshed = await this.tryRefresh();
        if (refreshed) {
          // Retry the original request with new token
          log.info('API', 'Retrying request after token refresh', { endpoint });
          return this.request<T>(endpoint, options, true);
        }
        log.warn('API', 'Token refresh failed, request not retried', { endpoint });
      }

      // If validation error has details, build a readable message from them
      if (errorData.details && typeof errorData.details === 'object') {
        const messages = Object.values(errorData.details).flat();
        if (messages.length > 0) {
          lancia(messages.join('. '));
        }
      }
      lancia(errorData.message || 'Request failed');
    }

    /*
     * Anche la lettura del corpo puo' trovare la rete caduta: Safari riceve il
     * 200 e poi lancia `TypeError: Load failed` qui (27 set, Mac che si
     * risveglia). Stesso trattamento della `fetch` qui sopra, altrimenti passa
     * grezzo, senza codice.
     */
    let text: string;
    try {
      text = await response.text();
    } catch (err) {
      if (err instanceof TypeError) {
        log.warn('API', 'network dropped while reading the response body', { endpoint });
        throw new Error(NETWORK_ERROR_CODE, { cause: err });
      }
      throw err;
    }

    // Handle empty responses
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      log.error('API', 'JSON.parse failed on response body', { endpoint });
      throw e;
    }
  }

  /**
   * Deduplicated token refresh. If a refresh is already in flight, callers
   * wait for the same promise instead of firing a second HTTP request (which
   * would use the already-revoked refresh token and fail).
   *
   * This is the ONLY entry point for token refresh — both the 401 interceptor
   * and the public `refreshTokenRequest()` method funnel through here.
   */
  async rinnovaToken(): Promise<EsitoRinnovo> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      log.info('API', 'Refresh already in progress, waiting for existing promise');
      const atteso = await this.refreshPromise;
      this.ultimoEsitoRinnovo = atteso;
      return atteso;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      const result = await this.refreshPromise;
      this.ultimoEsitoRinnovo = result;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Lo stesso rinnovo, ridotto a «ce l'ha fatta?». Per chi non ha modo di
   * distinguere i due fallimenti — l'intercettore dei 401, che comunque non
   * ritenta, e il socket, che ritenta per conto suo.
   */
  async tryRefresh(): Promise<boolean> {
    return (await this.rinnovaToken()) === 'riuscito';
  }

  private async doRefresh(): Promise<EsitoRinnovo> {
    log.info('API', 'Token refresh started');
    // Vedi TETTO_RICHIESTA_RINNOVO_MS: senza questo taglio una rete che non
    // risponde e non fallisce avvelena la promessa condivisa per tutta la
    // sessione, e nessun rinnovo successivo parte piu'.
    const taglio = new AbortController();
    const tetto = setTimeout(() => { taglio.abort(); }, TETTO_RICHIESTA_RINNOVO_MS);
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: taglio.signal,
      });

      if (!response.ok) {
        /*
         * Tre codici parlano del token — vedi `EsitoRinnovo`. 401 e 403: il
         * server l'ha guardato e l'ha rifiutato. 400: `MISSING_REFRESH_TOKEN`,
         * il cookie non e' arrivato affatto, e ritentare non lo fa comparire.
         * Il 429 e' un caso a se': non e' un problema del server da riprovare,
         * e' il server che chiede di smettere. Ogni altro codice (500, 502,
         * 503) dice che il server ha avuto un problema suo, e ritentare fra un
         * attimo puo' funzionare.
         */
        const credenzialiMorte =
          response.status === 400 || response.status === 401 || response.status === 403;
        const esito: EsitoRinnovo = credenzialiMorte
          ? 'credenziali-morte'
          : response.status === 429 ? 'rallenta' : 'transitorio';
        log.warn('API', 'Token refresh failed', {
          status: response.status,
          credentialsIncluded: true,
          esito,
        });
        return esito;
      }

      const data = await response.json();
      this.accessToken = data.accessToken;

      // Notify store of new token
      if (this.onTokenRefreshed) {
        this.onTokenRefreshed(data.accessToken);
      }

      log.info('API', 'Token refresh successful');
      return 'riuscito';
    } catch (error) {
      /*
       * `fetch` lancia quando la richiesta non arriva a destinazione (rete
       * assente, DNS, TLS) o quando il corpo della risposta non e' il JSON
       * atteso. In nessuno di questi casi il server ha detto che il token e'
       * morto: non si slogga nessuno.
       */
      log.error('API', 'Token refresh error', error);
      return 'transitorio';
    } finally {
      clearTimeout(tetto);
    }
  }

  // Auth endpoints
  async register(input: RegisterInput): Promise<RegisterResponse> {
    const response = await this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response;
  }

  async login(input: LoginInput): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response;
  }

  async guestLogin(): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/guest', {
      method: 'POST',
    });
    return response;
  }

  async upgradeGuest(input: UpgradeInput): Promise<AuthResponse | RegisterResponse> {
    const response = await this.request<AuthResponse | RegisterResponse>('/auth/upgrade', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response;
  }

  async logout(): Promise<void> {
    await this.request<void>('/auth/logout', { method: 'POST' });
  }

  /**
   * Public refresh entry point used by auth-store.
   * Routes through tryRefresh() to guarantee deduplication — if the 401
   * interceptor already kicked off a refresh, this call piggybacks on it.
   */
  async refreshTokenRequest(): Promise<boolean> {
    return this.tryRefresh();
  }

  async getMe(opts?: { signal?: AbortSignal }): Promise<{ user: User }> {
    const response = await this.request<{ user: User }>('/auth/me', { signal: opts?.signal });
    return response;
  }

  /**
   * L'export GDPR. `fetch` nuda per forza — la risposta è un file, non JSON, e
   * `request()` fa `response.json()` — ma con il RINNOVO che `request()` ha e
   * questa non aveva.
   *
   * L'access token dura quindici minuti e non è persistito: chi apre la pagina,
   * legge, e poi chiede l'export ha ottime probabilità di trovarne uno scaduto. Il
   * risultato era un 401 tradotto in «Export failed», senza ritentata e senza
   * spiegazione — su una funzione che per legge (art. 15/20 GDPR) deve funzionare.
   *
   * Il rinnovo qui è UNO solo: se anche quello non basta, la sessione è morta
   * davvero e l'errore va detto. L'errore porta ora anche lo stato, così chi lo
   * mostra può distinguere «riaccedi» da «riprova».
   */
  async exportData(): Promise<Blob> {
    const chiedi = (): Promise<Response> => fetch(`${API_URL}/auth/export-data`, {
      headers: this.accessToken ? { 'Authorization': `Bearer ${this.accessToken}` } : {},
      credentials: 'include',
    });

    let response = await chiedi();
    if (response.status === 401) {
      log.warn('API', 'export-data: 401, provo a rinnovare la sessione');
      if (await this.tryRefresh()) response = await chiedi();
    }
    if (!response.ok) {
      log.error('API', 'export-data fallita', { status: response.status });
      throw buildApiError('Export failed', response.status);
    }
    return response.blob();
  }

  async deleteAccount(password: string): Promise<void> {
    await this.request<{ success: boolean }>('/auth/delete-account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request<{ success: boolean }>('/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async updateAvatar(avatar: string | null): Promise<{ avatar: string | null }> {
    return this.request<{ avatar: string | null }>('/profile/me/avatar', {
      method: 'PATCH',
      body: JSON.stringify({ avatar }),
    });
  }

  async updateCountry(country: string | null): Promise<{ country: string | null }> {
    return this.request<{ country: string | null }>('/profile/me/country', {
      method: 'PATCH',
      body: JSON.stringify({ country }),
    });
  }

  async getPreferences(): Promise<UserPreferences> {
    const response = await this.request<{ preferences: UserPreferences }>('/user/preferences');
    return response.preferences;
  }

  async updatePreferences(prefs: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await this.request<{ preferences: UserPreferences }>('/user/preferences', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    });
    return response.preferences;
  }

  /**
   * «Questa app è installata su questa piattaforma», detto dall'app stessa.
   *
   * Serve a iOS, dove lo storage del web app in schermata Home è separato da
   * Safari: la memoria locale dell'installazione non raggiunge mai la scheda del
   * browser, e l'account è l'unico canale che attraversa quel confine. La
   * risposta la legge solo chi vuole: il chiamante spara e dimentica.
   */
  async reportPwaInstalled(platform: string): Promise<string[]> {
    const response = await this.request<{ platforms: string[] }>('/user/pwa-installed', {
      method: 'POST',
      body: JSON.stringify({ platform }),
    });
    return response.platforms;
  }

  /**
   * La verifica riuscita CONSEGNA UNA SESSIONE, come un accesso: chi arriva con
   * un gettone valido ha appena provato che l'indirizzo e' suo. Il gettone di
   * rinnovo resta nel cookie httpOnly e non compare qui, esattamente come in
   * `login`.
   */
  async verifyEmail(token: string): Promise<{ message: string } & AuthResponse> {
    return this.request<{ message: string } & AuthResponse>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(identifier: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    });
  }

  /**
   * Corregge l'indirizzo di un account non ancora verificato.
   *
   * Serve la password perché chi è in quello stato non può accedere: `login`
   * rifiuta gli account non verificati, quindi non c'è nessuna sessione da usare
   * come prova. Vedi `changeUnverifiedEmail` lato server.
   */
  async changeUnverifiedEmail(
    identifier: string,
    password: string,
    newEmail: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/change-unverified-email', {
      method: 'POST',
      body: JSON.stringify({ identifier, password, newEmail }),
    });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Profile endpoint
  async getProfile(username: string, opts?: { signal?: AbortSignal }): Promise<PublicProfile> {
    return this.request<PublicProfile>(`/profile/${encodeURIComponent(username)}`, { signal: opts?.signal });
  }

  // Profile games endpoint (public)
  async getUserGames(
    username: string,
    options: { limit?: number; offset?: number; signal?: AbortSignal } & GamesFilters = {}
  ): Promise<GamesListResponse> {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', options.limit.toString());
    if (options.offset) params.set('offset', options.offset.toString());
    if (options.variant) params.set('variant', options.variant);
    if (options.timeControl) params.set('timeControl', options.timeControl);
    if (options.result) params.set('result', options.result);
    if (options.opponent) params.set('opponent', options.opponent);

    const queryString = params.toString();
    const url = `/profile/${encodeURIComponent(username)}/games${queryString ? `?${queryString}` : ''}`;
    return this.request<GamesListResponse>(url, { signal: options.signal });
  }

  // Profile stats endpoint (public)
  async getUserStats(username: string, opts?: { signal?: AbortSignal; variant?: string }): Promise<UserStatsResponse> {
    const params = opts?.variant ? `?variant=${opts.variant}` : '';
    return this.request<UserStatsResponse>(`/profile/${encodeURIComponent(username)}/stats${params}`, { signal: opts?.signal });
  }

  // Profile rating-detail endpoint (public): per-TC peak/delta/spark + 30d history
  async getRatingDetail(username: string, opts?: { signal?: AbortSignal; variant?: string }): Promise<RatingDetailResponse> {
    const params = opts?.variant ? `?variant=${encodeURIComponent(opts.variant)}` : '';
    return this.request<RatingDetailResponse>(`/profile/${encodeURIComponent(username)}/rating-detail${params}`, { signal: opts?.signal });
  }

  /**
   * La variante dell'ultima partita giocata dall'account (e quando), su cui si
   * apre «Gioca». `variant: null` per chi non ha ancora giocato.
   */
  async getUltimaVariante(opts?: { signal?: AbortSignal }): Promise<{ variant: string | null; at: string | null }> {
    return this.request<{ variant: string | null; at: string | null }>('/profile/me/ultima-variante', { signal: opts?.signal });
  }

  // Dashboard summary (authed): aggregated dashboard data for the logged-in user
  async getDashboardSummary<T = unknown>(opts: { variant: string; timeControl: string; signal?: AbortSignal }): Promise<T> {
    const url = `/profile/me/dashboard-summary?variant=${encodeURIComponent(opts.variant)}&timeControl=${encodeURIComponent(opts.timeControl)}`;
    return this.request<T>(url, { signal: opts.signal });
  }

  // Games endpoints
  async getMyGames(limit: number = 20, offset: number = 0): Promise<GamesListResponse> {
    return this.request<GamesListResponse>(`/games?limit=${limit}&offset=${offset}`);
  }

  async getGameDetails(gameId: string): Promise<GameDetails> {
    return this.request<GameDetails>(`/games/${encodeURIComponent(gameId)}`);
  }

  /**
   * Fetch a game's PDN export as plain text. Can't use `request()` (which forces
   * JSON); uses the same API base URL and auth/credentials handling.
   */
  async getGamePDN(gameId: string, notes?: 'mine'): Promise<string> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const qs = notes ? `?notes=${notes}` : '';
    const response = await fetch(`${API_URL}/games/${encodeURIComponent(gameId)}/pdn${qs}`, {
      headers,
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`Failed to download PDN: ${response.status}`);
    return response.text();
  }

  // Leaderboard endpoints
  async getLeaderboard(
    limit: number = 100,
    timeControl?: TimeControlCategory,
    variant: string = 'international'
  ): Promise<LeaderboardResponse> {
    const params = new URLSearchParams({ limit: limit.toString(), variant });
    if (timeControl) {
      params.set('timeControl', timeControl);
    }
    return this.request<LeaderboardResponse>(`/leaderboard?${params.toString()}`);
  }

  // Most active players (by games played, per variant — no time-control axis)
  /**
   * `days` e' un filtro come `variant`, non un'opzione di trasporto: sta fra i
   * posizionali. `null` = di sempre. Con `fallbackToAllTime` il server, se la
   * finestra e' vuota, ripiega su «di sempre» e lo dichiara nella risposta
   * (`days` effettivo + `fellBack`): lo chiede la card, non la Classifica.
   */
  /**
   * L'avviso di servizio della home (manutenzione, riavvio programmato,
   * «STAGING»). Pubblica: la legge anche chi non ha un account.
   *
   * Risponde sempre 200 — un avviso spento e' `{ avviso: null }`, non un 404 —
   * perche' un errore qui finirebbe in Sentry a ogni apertura della home.
   */
  async getAvvisoDiServizio(opts?: { signal?: AbortSignal }): Promise<RispostaAvviso> {
    return this.request<RispostaAvviso>('/avviso', { signal: opts?.signal });
  }

  async getMostGamesLeaderboard(
    limit: number = 10,
    variant: string = 'international',
    days: number | null = null,
    opts?: { signal?: AbortSignal; fallbackToAllTime?: boolean }
  ): Promise<MostGamesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      variant,
      days: days === null ? 'all' : String(days),
    });
    if (opts?.fallbackToAllTime) params.set('fallback', '1');
    return this.request<MostGamesResponse>(`/leaderboard/most-games?${params.toString()}`, {
      signal: opts?.signal,
    });
  }

  /**
   * Classifica per giorni consecutivi di attività. Nessun `variant` né
   * `timeControl`: la streak conta anche i problemi risolti, che una variante
   * non ce l'hanno.
   */
  async getStreakLeaderboard(
    limit: number = 100,
    opts?: { signal?: AbortSignal }
  ): Promise<StreakResponse> {
    return this.request<StreakResponse>(`/leaderboard/streak?limit=${limit}`, {
      signal: opts?.signal,
    });
  }

  async getUserBadges(userId: string): Promise<{ badges: UserBadge[] }> {
    return this.request<{ badges: UserBadge[] }>(`/leaderboard/badges/${encodeURIComponent(userId)}`);
  }

  async getUserMonthlyPosition(
    userId: string,
    timeControl: TimeControlCategory = 'blitz'
  ): Promise<{ rank: number | null; total: number }> {
    return this.request<{ rank: number | null; total: number }>(
      `/leaderboard/monthly/position/${encodeURIComponent(userId)}?timeControl=${timeControl}`
    );
  }

  // Tournament endpoints
  async getTournaments(): Promise<{ tournaments: TournamentListItem[] }> {
    return this.request<{ tournaments: TournamentListItem[] }>('/tournaments');
  }

  async getTournament(id: string): Promise<TournamentDetail> {
    return this.request<TournamentDetail>(`/tournaments/${encodeURIComponent(id)}`);
  }

  async createTournament(
    input: CreateTournamentInput
  ): Promise<{ tournament: CreatedTournament }> {
    return this.request<{ tournament: CreatedTournament }>('/tournaments', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Annulla un torneo non ancora partito. Solo chi l'ha aperto. */
  async cancelTournament(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      `/tournaments/${encodeURIComponent(id)}/cancel`,
      { method: 'POST' }
    );
  }

  /**
   * Rifa' gettone d'invito e password di un torneo privato non ancora partito.
   *
   * Le chiavi NUOVE tornano nella risposta: il vecchio link smette di
   * funzionare nello stesso istante, quindi chi rigenera deve avere subito
   * quello da rimandare.
   */
  async rigeneraChiaviTorneo(
    id: string,
    joinPassword: string,
  ): Promise<{ success: boolean; inviteToken: string; joinPassword: string }> {
    return this.request<{ success: boolean; inviteToken: string; joinPassword: string }>(
      `/tournaments/${encodeURIComponent(id)}/rigenera-chiavi`,
      { method: 'POST', body: JSON.stringify({ joinPassword }) }
    );
  }

  /**
   * La credenziale serve solo ai tornei privati: la password detta a voce, o il
   * gettone che arriva col link. Su un torneo pubblico il corpo resta vuoto.
   */
  async joinTournament(
    id: string,
    credential?: { password?: string; token?: string }
  ): Promise<{ success: boolean; position: number }> {
    return this.request<{ success: boolean; position: number }>(
      `/tournaments/${encodeURIComponent(id)}/join`,
      { method: 'POST', body: JSON.stringify(credential ?? {}) }
    );
  }

  async getTournamentGames(
    id: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ games: TournamentGameEntry[] }> {
    return this.request<{ games: TournamentGameEntry[] }>(
      `/tournaments/${encodeURIComponent(id)}/games?limit=${limit}&offset=${offset}`
    );
  }

  async leaveTournament(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      `/tournaments/${encodeURIComponent(id)}/leave`,
      { method: 'POST' }
    );
  }

  // Arena endpoints

  /**
   * Apre un'arena. Gemella di `createTournament`, e la differenza sta tutta in
   * `durationMinutes`: un torneo non lo manda (il server lo ricava dai turni),
   * un'arena lo deve mandare, perche' non c'e' niente da cui ricavarlo.
   */
  async createArena(input: CreateArenaInput): Promise<{ arena: CreatedArena }> {
    return this.request<{ arena: CreatedArena }>('/arenas', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Annulla un'arena non ancora partita. Solo chi l'ha aperta. */
  async cancelArena(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      `/arenas/${encodeURIComponent(id)}/cancel`,
      { method: 'POST' }
    );
  }

  async getArenas(): Promise<{ arenas: ArenaListItem[] }> {
    return this.request<{ arenas: ArenaListItem[] }>('/arenas');
  }

  async getArena(id: string): Promise<ArenaDetail> {
    return this.request<ArenaDetail>(`/arenas/${encodeURIComponent(id)}`);
  }

  /**
   * Pagine profonde della classifica. Il socket ne manda venti: dalla
   * ventunesima in poi si chiedono qui.
   */
  async getArenaStandings(
    id: string,
    offset: number = 0,
    limit: number = 20
  ): Promise<{ standings: ArenaStandingEntry[]; participantCount: number; righeInClassifica?: number; offset: number; limit: number }> {
    return this.request<{ standings: ArenaStandingEntry[]; participantCount: number; righeInClassifica?: number; offset: number; limit: number }>(
      `/arenas/${encodeURIComponent(id)}/standings?limit=${limit}&offset=${offset}`
    );
  }

  async getArenaGames(
    id: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ games: ArenaGameEntry[] }> {
    return this.request<{ games: ArenaGameEntry[] }>(
      `/arenas/${encodeURIComponent(id)}/games?limit=${limit}&offset=${offset}`
    );
  }

  // Support / contact form
  async submitSupportRequest(input: SupportRequestInput): Promise<{ success: boolean; id?: string }> {
    return this.request<{ success: boolean; id?: string }>('/support/submit', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

}

export type SupportCategory = 'login' | 'variant_request' | 'rules' | 'other';

export interface SupportRequestInput {
  email: string;
  name?: string;
  category: SupportCategory;
  subject: string;
  message: string;
  // Honeypot — clients should always send empty string.
  website?: string;
}

export const api = new ApiClient();
